import { toast } from "dash-ui";
import {
  GetBrokerByIdQuery,
  GetPortfoliosByPublicFolderQuery,
  useSendMessageToAgentMutation,
} from "gql/generated.graphql";
import { FunctionComponent, MutableRefObject, useEffect } from "react";
import { useForm } from "react-hook-form";
import { PiGlobeHemisphereWestFill } from "react-icons/pi";
import { MdPhone } from "react-icons/md";
import { formatByMask } from "dash-common";

// Main component props type
type AgentContactCardProps = {
  // I could not type just the broker and portfolio data, so I receive the entire queries in the component
  getBrokerQuery?: GetBrokerByIdQuery;
  getPortfolioQuery?: GetPortfoliosByPublicFolderQuery;
  propertyId?: string;
  onCheckAvailability: () => void;
  contactFormRef: MutableRefObject<HTMLInputElement | null>;
};
// Form type
type ContactFormData = {
  name: string;
  email: string;
  phone?: string;
  message: string;
};

// Main Component
export const AgentContactCard: FunctionComponent<AgentContactCardProps> = (
  props
) => {
  // Abstract broker data from query data
  const broker = props.getBrokerQuery?.getBrokerById;
  // Abstract portfolio data from query data
  const portfolio =
    props.getPortfolioQuery?.getPortfoliosByPublicFolder.collection?.find(
      function (data) {
        return data.property?._id === props.propertyId;
      }
    );
  // Hooks
  const contactForm = useForm<ContactFormData>();
  // Queries and mutations
  const [sendMessageToAgent] = useSendMessageToAgentMutation();
  // Contact broker functionality
  async function onContactBroker(formData: ContactFormData) {
    if (!broker || !portfolio) return;
    await sendMessageToAgent({
      variables: {
        input: {
          broker_email: broker.account.email,
          broker_name: broker.profile.firstName,
          customer_email: formData.email,
          customer_message: formData.message,
          customer_name: props.contactFormRef.current?.value ?? formData.name,
          customer_phone: formData.phone || "",
          property_name: portfolio.property?.name || "",
          portfolio_folder_name: portfolio.portfolioFolder?.name || "",
        },
      },
    });
    contactForm.reset();
    toast.success(
      `Your message has been sent and the agent will contact you shortly.`
    );
    return;
  }
  // Do not render content if do not have those properties
  if (!broker || !portfolio) return <></>;

  // Return HTML component
  return (
    <div className="flex flex-col bg-white rounded-md w-64 relative overflow-clip mt-4 p-4 shadow-md text-sm">
      {broker.profile.avatarFile ? (
        <img
          src={broker.profile.avatarFile.publicUrl}
          alt="Broker Image"
          className="aspect-[4/3] object-cover rounded-xl"
        />
      ) : (
        <div className="flex h-96 w-96 bg-slate-200 rounded-full absolute -top-32 place-self-center overflow-clip">
          <img
            src="/images/dash_logo_compact.png"
            className="h-64 w-64 object-contain mt-auto mx-auto top-20"
            alt="Dash logo"
          />
        </div>
      )}
      <div
        className={`flex flex-col text-slate-800 mt-4 ${
          broker.profile.avatarFile ? "" : "mt-64"
        }`}>
        <div className="flex flex-col gap-4 mb-4">
          <div>
            <p className="font-bold text-2xl">
              {broker.profile.firstName} {broker.profile.lastName}
            </p>
            {broker.agencyName && (
              <p className="font-semibold">{broker.agencyName}</p>
            )}
            {broker.description && (
              <p className="text-sm text-slate-600 mt-2">
                {broker.description}
              </p>
            )}
          </div>
          {broker.agencyWebsite && (
            <a
              href={broker.agencyWebsite || ""}
              className="text-slate-500 flex items-center gap-2">
              <PiGlobeHemisphereWestFill className="h-4 w-4" /> Visit Website
            </a>
          )}
        </div>
        {broker.account.phone && (
          <div className="flex flex-col gap-4 mb-4">
            <span className="uppercase text-slate-500 font-semibold">
              Contact me
            </span>
            <a
              href={`tel:${broker.account.phone?.dialCode}${broker.account.phone?.number}`}
              className="bg-sky-600 h-10 rounded text-white flex items-center justify-center gap-2 px-4">
              <MdPhone className="h-4 w-4" /> {broker.account.phone?.dialCode}{" "}
              {formatByMask(broker.account.phone?.number, "(###) ###-####")}
            </a>
          </div>
        )}
        <form
          className="flex flex-col gap-2"
          onSubmit={contactForm.handleSubmit(onContactBroker)}>
          <label className="text-slate-800 font-semibold text-sm">
            Your name*
            <input
              className="w-full border border-slate-300 rounded h-10 px-4 text-slate-800 text-sm font-normal focus:outline-none"
              {...contactForm.register("name")}
              required
              ref={props.contactFormRef}
            />
          </label>
          <label className="text-slate-800 font-semibold text-sm">
            Email*
            <input
              className="w-full border border-slate-300 rounded h-10 px-4 text-slate-800 text-sm font-normal focus:outline-none"
              {...contactForm.register("email")}
              required
              type="email"
            />
          </label>
          <label className="text-slate-800 font-semibold text-sm">
            Phone
            <input
              className="w-full border border-slate-300 rounded h-10 px-4 text-slate-800 text-sm font-normal focus:outline-none"
              {...contactForm.register("phone")}
            />
          </label>
          <label className="text-slate-800 font-semibold text-sm">
            Message*
            <textarea
              className="w-full border border-slate-300 rounded h-28 px-4 text-slate-800 text-sm font-normal focus:outline-none"
              required
              {...contactForm.register("message")}
            />
          </label>
          <button
            type="submit"
            className="bg-sky-600 h-10 rounded text-white mt-2">
            Send Message
          </button>
        </form>
      </div>
    </div>
  );
};
